/**
 * Application-wide Constants
 *
 * Centralized location for all magic numbers, strings, and configuration values
 * used throughout the application.
 */

// ========================================
// HTTP Status Codes
// ========================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// ========================================
// API Configuration
// ========================================

export const API_CONFIG = {
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// ========================================
// Authentication
// ========================================

export const AUTH_CONFIG = {
  TOKEN_BUFFER_SECONDS: 30, // Token expiration buffer for safety
  SESSION_CHECK_INTERVAL: 60000, // Check token every minute
};

// ========================================
// UI/UX Constants
// ========================================

export const UI_CONSTANTS = {
  // Modal/Dialog sizes
  MODAL_WIDTH_SMALL: 400,
  MODAL_WIDTH_MEDIUM: 600,
  MODAL_WIDTH_LARGE: 800,

  // List/Table limits
  DEFAULT_PAGE_SIZE: 20,
  MAX_LIST_HEIGHT: 200,

  // Debounce/Throttle delays
  DEBOUNCE_DELAY: 300, // milliseconds
  THROTTLE_DELAY: 500, // milliseconds

  // Animation durations
  ANIMATION_FAST: 150, // milliseconds
  ANIMATION_NORMAL: 300, // milliseconds
  ANIMATION_SLOW: 500, // milliseconds
};

// ========================================
// File Upload
// ========================================

export const FILE_UPLOAD = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10 MB
  ALLOWED_EXTENSIONS: ['.pdf', '.doc', '.docx', '.txt'],
  CHUNK_SIZE: 1024 * 1024, // 1 MB chunks
};

// ========================================
// Validation
// ========================================

export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  MIN_USERNAME_LENGTH: 3,
  MAX_USERNAME_LENGTH: 50,
  MAX_EMAIL_LENGTH: 255,
};

// ========================================
// Error Messages
// ========================================

export const ERROR_MESSAGES = {
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'Access denied. Please refresh the page and try again.',
  NOT_FOUND: 'The requested resource was not found.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
};

// ========================================
// Local Storage Keys
// ========================================

export const STORAGE_KEYS = {
  USER: 'user',
  AI_COACH_STATE: 'rlc-aicoach-state',
  REPORT_WRITER_STATE: 'rlc-reportwriter-state',
  THEME: 'theme',
  LANGUAGE: 'language',
};

// ========================================
// Feature Flags
// ========================================

export const FEATURES = {
  AI_COACH_ENABLED: true,
  REPORT_WRITER_ENABLED: true,
  ARCHIVE_ENABLED: true,
  FEEDBACK_ENABLED: true,
};

// ========================================
// Endpoints (relative paths)
// ========================================

export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/v1/auth/token',
    REGISTER: '/api/v1/auth/register',
    LOGOUT: '/api/v1/auth/logout',
    REFRESH: '/api/v1/auth/refresh',
    CSRF_TOKEN: '/api/v1/auth/csrf-token',
  },
  AI_COACH: {
    MESSAGES: '/api/v1/coach/messages',
    CLEAR: '/api/v1/coach/clear',
  },
  REPORT_WRITER: {
    GENERATE: '/api/v1/report/generate',
  },
  ARCHIVE: {
    PROJECTS: '/api/v1/archive/projects',
    DOCUMENTS: '/api/v1/archive/documents',
  },
};

export default {
  HTTP_STATUS,
  API_CONFIG,
  AUTH_CONFIG,
  UI_CONSTANTS,
  FILE_UPLOAD,
  VALIDATION,
  ERROR_MESSAGES,
  STORAGE_KEYS,
  FEATURES,
  ENDPOINTS,
};
